<template>
	<base-select
		:id="id"
		:value="value"
		:selected="selected"
		:options="options"
		:allowEmpty="allowEmpty"
		:showOnOneOption="showOnOneOption"
		v-on="$listeners"
		v-slot="{
			pSelectedOption,
			pOptions,
			pExpanded,
			pInitial,
			mOpen,
			mClose,
			mToggle,
			mSelect,
			mSelectFirst,
			mSelectLast,
			mSelectDown,
			mSelectUp,
			cOneOption
		}"
	>
		<div
			:id="id"
			ref="selectWrapper"
			v-bind="$attrs"
			v-bounds="bounds"
			:class="[
				'secondary-select',
				colorScheme,
				{ 'floating-label': hasFloatingLabel },
				{ 'single-option-select': !cOneOption }
			]"
		>
			<div class="listbox-anchor">
				<BaseSelectButton
					:id="buttonId"
					:aria-controls="listId"
					role="combobox"
					class="secondary-select-button"
					:class="[
						hasFloatingLabel ? 'float-button-label' : null,
						{ 'error-message': showRequiredFieldError }
					]"
					:expanded="pExpanded"
					:labelledBy="ariaLabelledBy"
					:initial="pInitial"
					:toggle="mToggle"
					:open="mOpen"
					:selectDown="mSelectDown"
					:selectUp="mSelectUp"
					:aria-describedby="
						showRequiredFieldError ? `${buttonId}-error` : null
					"
					@faset-focusout="onFocusout"
					:oneOption="cOneOption"
				>
					<span
						:id="labelId"
						class="secondary-select-label"
						:class="
							hideButtonLabel
								? 'visually-hidden'
								: floatButtonLabel
								? 'above'
								: null
						"
					>
						{{ label }}
					</span>
					<span
						class="inner"
						:data-rpa-tag-id="dataRPATagId"
						v-html="
							showLabelInButton
								? getOptionButtonLabel(pSelectedOption)
								: getButtonLabel(pSelectedOption)
						"
					/>
					<span
						:class="['icon', pExpanded ? 'icon-up' : 'icon-down']"
						aria-hidden="true"
					/>
				</BaseSelectButton>
				<BaseSelectList
					v-show="pExpanded"
					:id="listId"
					class="secondary-select-list"
					ref="selectList"
					:style="styles"
					:expanded="pExpanded"
					:labelledBy="labelId"
					:aria-required="requiredField"
					:close="mClose"
					:selectFirst="mSelectFirst"
					:selectLast="mSelectLast"
					:selectDown="mSelectDown"
					:selectUp="mSelectUp"
					:selectedOption="pSelectedOption"
					@list-focused="onListFocus"
					@list-blurred="onListBlur"
				>
					<BaseSelectOption
						v-for="(option, index) in pOptions"
						:key="index"
						class="secondary-select-option"
						:class="{ disabled: option.disabled }"
						:aria-disabled="option.disabled ? 'true' : null"
						ref="selectOptions"
						:id="option.id"
						:index="index"
						:selected="option.selected"
						:select="mSelect"
						@selected="onOptionSelected(index)"
						v-html="getOptionSelectLabel(option)"
					/>
				</BaseSelectList>
			</div>
			<div
				class="error-messages"
				:style="
					errorPlaceholderSpace
						? `height: ${errorPlaceholderSpace}px; margin-top: 8px`
						: null
				"
			>
				<div
					class="error"
					v-if="showRequiredFieldError"
					aria-live="polite"
					:id="`${buttonId}-error`"
				>
					<Icon type="warning" ariaLabel="Error" />
					<span v-html="requiredFieldError" class="p4 error__message" />
				</div>
			</div>
		</div>
	</base-select>
</template>

<script>
import { bounds } from "@vg-faset/core/js/directives";
import { Select } from "./mixins";
import BaseSelect from "./base/BaseSelect";
import BaseSelectButton from "./base/SelectButton";
import BaseSelectList from "./base/SelectList";
import BaseSelectOption from "./base/SelectOption";
import { Icon } from "../icon";

/**
 * A select component with a Secondary Select design theme.
 */
export default {
	name: "SecondarySelect",

	mixins: [Select],

	directives: {
		bounds
	},

	components: {
		BaseSelect,
		BaseSelectButton,
		BaseSelectList,
		BaseSelectOption,
		Icon
	},

	props: {
		/**
		 * Enter the maximum height of the drop-down list.
		 */
		maxHeight: {
			type: Number,
			default: 400
		},

		/**
		 * Sets whether the list should invert when the component is near the bottom of the viewport.
		 */
		invert: {
			type: Boolean,
			default: false
		},

		/**
		 * The offset from the bottom of the viewport which triggers the list to invert. If no value is set then the maxHeight prop value is used.
		 */
		invertOffset: {
			type: Number,
			default: 0
		},

		/**
		 * Allows label to float above value when an option is selected.
		 */
		hasFloatingLabel: {
			type: Boolean,
			default: false
		},

		/**
		 * If true, sets aria-required to `true` and displays error message if value is null on blur
		 */
		isRequiredField: {
			type: Boolean,
			default: false
		},

		/**
		 * Set custom error message to be displayed when required field is null on blur. If not defined, default message "This field is required" will be displayed.
		 */
		requiredFieldError: {
			type: String,
			default: "This field is required."
		},

		/**
		 * Set the number value of space (in pixels) to reserve below the Select for error messages. This helps keep the page elements below from moving when an error appears.
		 */
		errorPlaceholderSpace: {
			type: Number,
			default: undefined
		},

		/**
		 * The data-rpa-tag-id attribute value.
		 */
		dataRPATagId: {
			type: String,
			default: ""
		}
	},

	data() {
		return {
			isFocused: null,
			selectedOption: this.selected,
			hasRequiredAttribute: null
		};
	},

	computed: {
		requiredField() {
			return this.hasRequiredAttribute || this.isRequiredField;
		},

		styles() {
			return {
				maxHeight: `${this.maxHeight}px`
			};
		},

		bounds() {
			let bounds;

			if (this.invert) {
				if (this.invertOffset) {
					bounds = {
						bottom: Math.min(this.maxHeight, this.invertOffset)
					};
				} else {
					bounds = {
						bottom: this.maxHeight
					};
				}
			} else {
				bounds = false;
			}

			return bounds;
		},

		floatButtonLabel() {
			return (
				this.hasFloatingLabel &&
				this.showLabelInButton &&
				this.selectedOption > -1
			);
		},

		hideButtonLabel() {
			return (
				!this.showLabelInButton ||
				(!this.hasFloatingLabel &&
					this.showLabelInButton &&
					(this.value || this.isFocused))
			);
		},

		showRequiredFieldError() {
			return this.requiredField && !this.value && this.isFocused === false;
		}
	},

	methods: {
		// maybe update scroll position of select list if selected option
		// above or below the select list "window"
		onOptionSelected(index) {
			this.selectedOption = index;
			const $selectList = this.$refs.selectList.$el;
			const $selectOption = this.$refs.selectOptions[index].$el;

			const selectListBottom = $selectList.clientHeight + $selectList.scrollTop;
			const selectOptionBottom =
				$selectOption.offsetTop + $selectOption.offsetHeight;

			// scroll down to show selected option
			if (selectOptionBottom > selectListBottom) {
				$selectList.scrollTop = selectOptionBottom - $selectList.clientHeight;
			}

			// scroll up to show selected option
			if ($selectOption.offsetTop < $selectList.scrollTop) {
				$selectList.scrollTop = $selectOption.offsetTop;
			}
		},

		onListFocus() {
			this.isFocused = true;
		},

		onListBlur() {
			if (this.isFocused) {
				this.isFocused = false;
			}
		},

		onFocusout() {
			if (!this.value) {
				this.isFocused = false;
			}
		}
	},

	mounted() {
		this.hasRequiredAttribute = this.$refs.selectWrapper
			? this.$refs.selectWrapper.hasAttribute("required")
			: false;
	}
};
</script>

<style lang="scss" scoped>
.secondary-select {
	width: 100%;
	display: inline-block;
	margin-bottom: 16px;
}

.secondary-select-button {
	display: flex;
	justify-content: space-between;
	width: 100%;
	font-size: 11px;
	letter-spacing: 0.09em;
	line-height: 1.5;
	cursor: pointer;
	text-transform: uppercase;
	text-align: left;
	margin: 0;
	padding: 16px 19px 11px 20px;
	background-color: $color--white;
	border-top: 2px solid $color--lighter-gray;
	border-right: 2px solid $color--lighter-gray;
	border-bottom: 2px solid $color--lighter-gray;
	border-left: 2px solid $color--lighter-gray;
	border-radius: 0;

	&.active {
		border: 2px solid $color--lighter-gray;
		border-bottom: 2px solid transparent;

		.bottom & {
			border-top: none;
			border-bottom: 2px solid $color--lighter-gray;
			margin: 2px 0 0 0;
		}
	}

	.inner {
		flex-basis: auto;
	}

	.icon {
		display: inline-block;
		margin: 0 0 0 17px;
		font-size: 8px;
		line-height: 1.5;
		color: $color--red;
		vertical-align: top;
	}

	&.float-button-label {
		padding: 36px 16px 9px 16px;
		text-transform: initial;
		color: $color--neutral-2;
		line-height: initial;
		letter-spacing: 0;
		border-color: $color--neutral-7;

		&:focus-visible {
			outline: none;
			border: 2px solid $color--neutral-2;
		}

		&.active {
			border-top-color: $color--neutral-2;
			border-right-color: $color--neutral-2;
			border-left-color: $color--neutral-2;
			border-bottom: none;
		}

		.secondary-select-label {
			font-family: $font--default;
			font-size: 14px;
			letter-spacing: 0;
			left: 16px;
			right: initial;
			top: 50%;
			transform: translateY(-50%);
			pointer-events: none;
			position: absolute;
			transform-origin: left top;
			transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1),
				top transform 150ms cubic-bezier(0.4, 0, 0.2, 1),
				color 150ms cubic-bezier(0.4, 0, 0.2, 1);
			text-align: left;
			white-space: nowrap;
			cursor: text;
			overflow: hidden;
			will-change: transform;

			&.above {
				transform: translateY(0%) scale(0.78);
				top: 16px;
			}
		}

		.inner {
			text-transform: initial;
			font-size: 16px;
		}

		.icon {
			transform: translateY(-80%);
			line-height: 2.35;
		}

		&.error-message {
			border-color: $color--red-5;
		}
	}
}

.vg-is-tabbing {
	.secondary-select-button {
		&.float-button-label,
		&.float-button-label.error-message {
			&:focus {
				outline-color: Highlight; // IE11
				outline-style: solid; // IE11
				outline-color: $color--blue-outline; // Webkit, #005fcc
				outline-style: auto; // Webkit
				outline-width: medium;
			}
		}

		&.float-button-label {
			&.active {
				border-top-color: $color--blue-outline;
				border-right-color: $color--blue-outline;
				border-left-color: $color--blue-outline;
			}
		}
	}

	.floating-label {
		.secondary-select-list {
			&.active {
				border-bottom-color: $color--blue-outline;
				border-right-color: $color--blue-outline;
				border-left-color: $color--blue-outline;
			}

			.secondary-select-option {
				&.selected {
					outline-color: Highlight; // IE11
					outline-style: solid; // IE11
					outline-color: $color--blue-outline; // Webkit
					outline-style: auto; // Webkit
					outline-width: medium;
				}
			}
		}
	}

	.secondary-select-button {
		&.active {
			border-color: $color--blue-outline;
			border-bottom: none;
		}
	}
	.secondary-select-list {
		&.active {
			border-color: $color--blue-outline;
			border-top-color: transparent;
		}
		.secondary-select-option {
			&.selected {
				outline-color: Highlight; // IE11
				outline-style: solid; // IE11
				outline-color: $color--blue-outline; // Webkit
				outline-style: auto; // Webkit
				outline-width: medium;
			}
		}
	}
}

.secondary-select-list {
	display: none;
	position: absolute;
	top: 100%;
	left: 0;
	z-index: 3;
	width: 100%;
	min-width: auto;
	min-width: initial;
	list-style-type: none;
	background-color: $color--white;
	border-top: 2px solid $color--lighter-gray;
	border-right: 2px solid $color--lighter-gray;
	border-bottom: 2px solid $color--lighter-gray;
	border-left: 2px solid $color--lighter-gray;
	border-radius: 0;
	overflow-y: auto;
	opacity: 0;

	&.active {
		display: block;
		opacity: 1;
		border-top: none;
		outline: none;
	}

	.bottom & {
		top: auto;
		bottom: 100%;
		border-top: 2px solid $color--lighter-gray;
		border-bottom: none;
	}

	&.disabled {
		color: $color--light-gray;
		pointer-events: none;
	}
}

.secondary-select-option {
	margin: 0;
	padding: 14px 20px 14px 20px;
	color: $color--neutral-2;
	font-size: 15px;
	line-height: 1;
	cursor: pointer;

	&:hover {
		background: $color--neutral-8;
		color: $color--black;
	}

	&.selected {
		background: $color--neutral-8;
		color: $color--black;
	}

	&::v-deep a {
		color: inherit;
	}
}

.listbox-anchor {
	position: relative;
}

.error {
	margin-top: 8px;
	color: $color--red-5;
}

.error__message {
	vertical-align: top;
	margin-left: 8px;
}

.floating-label {
	.secondary-select-list {
		&.active {
			border-right-color: $color--neutral-2;
			border-bottom-color: $color--neutral-2;
			border-left-color: $color--neutral-2;
		}
	}
}

// color schemes
.inverted {
	.secondary-select-button {
		background-color: $color--neutral-2;
		border: 2px solid $color--neutral-2;

		&.active {
			margin: 0;
		}

		.inner {
			color: $color--white;
		}

		.icon {
			color: $color--white;
		}
	}

	.secondary-select-list {
		background-color: $color--neutral-2;
		border: 0;

		&.disabled {
			color: $color--light-gray;
		}
	}

	.secondary-select-option {
		background-color: $color--neutral-2;
		color: $color--white;

		&:hover {
			background-color: $color--neutral-5;
			color: $color--white;
		}

		&.selected {
			background-color: $color--neutral-5;
			color: $color--white;
		}

		&::v-deep a {
			color: inherit;
		}
	}
}

// Hide icon if dropdown is disabled on one option.
.single-option-select {
	button {
		span {
			&.icon {
				&.icon-down {
					display: none;
				}
			}
		}
	}
}
</style>
